import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

// Helper function to determine league status based on dates
const getLeagueStatus = (startDate: string, endDate: string): { status: 'upcoming' | 'active' | 'completed', color: string, bgColor: string } => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (now < start) {
    return { status: 'upcoming', color: 'text-yellow-300', bgColor: 'bg-yellow-500/20' };
  } else if (now >= start && now <= end) {
    return { status: 'active', color: 'text-green-300', bgColor: 'bg-green-500/20' };
  } else {
    return { status: 'completed', color: 'text-gray-300', bgColor: 'bg-gray-500/20' };
  }
};

interface Bowler {
  id: number;
  name: string;
  email: string;
  phone: string;
  city: string;
  country: string;
  gender: string | null;
  dob: string | null;
  created_at: string;
  is_subscriber: boolean;
  subscription_expiration: string;
  league_count: number;
}

interface BowlerLeague {
  league_id: number;
  league_name: string;
  league_venue: string;
  league_city: string;
  league_country: string;
  team_id: number;
  team_name: string;
  member_role: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
}

interface BowlerStats {
  teamInfo: {
    team_id: number;
    team_name: string;
    member_role: string;
  };
  teamMembers: Array<{
    player_id: number;
    player_name: string;
    player_email: string;
    city: string;
    country: string;
    member_role: string;
    is_subscriber: boolean;
    subscription_expiration: string;
    payment_status: string;
    amount_paid: number;
  }>;
  bowlerStats: {
    average_score: number;
    average_handicap: number;
    total_games: number;
    total_pins: number;
  };
  leagueSettings: {
    handicap_enabled: boolean;
    handicap_base: number;
    handicap_percentage: number;
    handicap_max: number;
  };
}

interface OtherParticipant {
  team_id: number;
  team_name: string;
  player_id: number;
  player_name: string;
  player_email: string;
  city: string;
  country: string;
  member_role: string;
  is_subscriber: boolean;
  subscription_expiration: string;
  payment_status: string;
  average_score: number;
  average_handicap: number;
}

const MasterBowlers: React.FC = () => {
  const { t } = useTranslation();
  
  const [bowlers, setBowlers] = useState<Bowler[]>([]);
  const [selectedBowler, setSelectedBowler] = useState<Bowler | null>(null);
  const [bowlerLeagues, setBowlerLeagues] = useState<BowlerLeague[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<BowlerLeague | null>(null);
  const [bowlerStats, setBowlerStats] = useState<BowlerStats | null>(null);
  
  const [loadingBowlers, setLoadingBowlers] = useState(true);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Search state
  const [bowlerSearch, setBowlerSearch] = useState('');
  const [filterZeroLeagues, setFilterZeroLeagues] = useState(false);

  // Delete bowler state
  const [deletingBowlerId, setDeletingBowlerId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  
  // Modals
  const [showOtherParticipantsModal, setShowOtherParticipantsModal] = useState(false);
  const [otherParticipants, setOtherParticipants] = useState<OtherParticipant[]>([]);

  // Edit bowler modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', city: '', country: '', gender: '', dob: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Set password modal
  const [showSetPwdModal, setShowSetPwdModal] = useState(false);
  const [setPwdTarget, setSetPwdTarget] = useState<{ id: number; name: string; email: string } | null>(null);
  const [setPwdValue, setSetPwdValue] = useState('');
  const [setPwdLoading, setSetPwdLoading] = useState(false);
  const [setPwdError, setSetPwdError] = useState<string | null>(null);
  const [setPwdSuccess, setSetPwdSuccess] = useState(false);

  useEffect(() => {
    fetchBowlers();
  }, []);

  const fetchBowlers = async () => {
    try {
      setLoadingBowlers(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/bowlers/bowlers`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        
        setBowlers(data.bowlers);
      }
    } catch (error) {
      console.error('Failed to fetch bowlers:', error);
    } finally {
      setLoadingBowlers(false);
    }
  };

  const fetchBowlerLeagues = async (bowlerId: number) => {
    try {
      setLoadingLeagues(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/bowlers/bowlers/${bowlerId}/leagues`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBowlerLeagues(data.leagues);
      }
    } catch (error) {
      console.error('Failed to fetch bowler leagues:', error);
    } finally {
      setLoadingLeagues(false);
    }
  };

  const fetchBowlerStats = async (bowlerId: number, leagueId: number) => {
    try {
      setLoadingStats(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/bowlers/bowlers/${bowlerId}/leagues/${leagueId}/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBowlerStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch bowler stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchOtherParticipants = async (bowlerId: number, leagueId: number) => {
    const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
    const response = await fetch(`${apiUrl}/master/bowlers/bowlers/${bowlerId}/leagues/${leagueId}/other-participants`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.ok) {
      const data = await response.json();
      setOtherParticipants(data.participants);
      setShowOtherParticipantsModal(true);
    }
  };

  const handleBowlerClick = (bowler: Bowler) => {
    setSelectedBowler(bowler);
    setSelectedLeague(null);
    setBowlerStats(null);
    fetchBowlerLeagues(bowler.id);
  };

  const handleDeleteBowler = async (bowler: Bowler) => {
    setDeletingBowlerId(bowler.id);
    setDeleteError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/master/bowlers/bowlers/${bowler.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setDeleteError(data.error || 'Failed to delete bowler.');
      } else {
        setBowlers(prev => prev.filter(b => b.id !== bowler.id));
        if (selectedBowler?.id === bowler.id) {
          setSelectedBowler(null);
          setBowlerLeagues([]);
          setBowlerStats(null);
        }
      }
    } catch {
      setDeleteError('Network error. Please try again.');
    } finally {
      setDeletingBowlerId(null);
      setDeleteConfirmId(null);
    }
  };



  const handleSaveEdit = async () => {
    if (!selectedBowler) return;
    setSavingEdit(true);
    setEditError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/bowlers/bowlers/${selectedBowler.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        const updated: Bowler = { ...selectedBowler, ...editForm };
        setSelectedBowler(updated);
        setBowlers(prev => prev.map(b => b.id === updated.id ? updated : b));
        setShowEditModal(false);
      } else {
        const data = await response.json();
        setEditError(data.error || 'Failed to update bowler');
      }
    } catch (error) {
      setEditError('Network error. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const openSetPwdModal = (bowler: { id: number; name: string; email: string }) => {
    setSetPwdTarget(bowler);
    setSetPwdValue('');
    setSetPwdError(null);
    setSetPwdSuccess(false);
    setShowSetPwdModal(true);
  };

  const handleSetPassword = async () => {
    if (!setPwdTarget) return;
    if (!setPwdValue || setPwdValue.length < 6) {
      setSetPwdError('Password must be at least 6 characters.');
      return;
    }
    setSetPwdLoading(true);
    setSetPwdError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/master/bowlers/bowlers/${setPwdTarget.id}/set-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: setPwdValue })
      });
      if (response.ok) {
        setSetPwdSuccess(true);
      } else {
        const data = await response.json();
        setSetPwdError(data.error || 'Failed to set password.');
      }
    } catch {
      setSetPwdError('Network error. Please try again.');
    } finally {
      setSetPwdLoading(false);
    }
  };

  const handleLeagueClick = (league: BowlerLeague) => {
    setSelectedLeague(league);
    if (selectedBowler) {
      fetchBowlerStats(selectedBowler.id, league.league_id);
    }
  };

  // Group other participants by team
  const groupedOtherParticipants = otherParticipants.reduce((acc, p) => {
    if (!acc[p.team_id]) {
      acc[p.team_id] = {
        team_name: p.team_name,
        members: []
      };
    }
    acc[p.team_id].members.push(p);
    return acc;
  }, {} as Record<number, { team_name: string; members: OtherParticipant[] }>);

  // Filter bowlers by search and optional zero-leagues toggle
  const filteredBowlers = bowlers.filter(bowler => {
    const matchesSearch =
      (bowler.name ?? '').toLowerCase().includes(bowlerSearch.toLowerCase()) ||
      (bowler.email ?? '').toLowerCase().includes(bowlerSearch.toLowerCase()) ||
      bowler.id.toString().includes(bowlerSearch);
    const matchesFilter = !filterZeroLeagues || bowler.league_count === 0;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-3 lg:gap-6 lg:h-[calc(100vh-300px)]">

      {/* Bowler List */}
      <div className="lg:col-span-1 bg-white/10 backdrop-blur-lg rounded-xl p-4 sm:p-6 border border-white/20 overflow-y-auto">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">
          {t('superadmin.bowlers.title')} ({bowlers.length})
        </h3>

        <div className="mb-4 space-y-2">
          <input
            type="text"
            placeholder="Search by name, email, or ID..."
            value={bowlerSearch}
            onChange={(e) => setBowlerSearch(e.target.value)}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-green-200/50 focus:outline-none focus:border-green-400 text-sm"
          />
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={filterZeroLeagues}
              onChange={e => setFilterZeroLeagues(e.target.checked)}
              className="w-4 h-4 rounded border-white/30 bg-white/10 text-red-500 focus:ring-red-500"
            />
            <span className="text-green-200 text-xs">Show only bowlers with 0 leagues</span>
          </label>
          {deleteError && (
            <div className="px-3 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 text-xs">
              {deleteError}
              <button onClick={() => setDeleteError(null)} className="ml-2 underline">Dismiss</button>
            </div>
          )}
        </div>

        {loadingBowlers ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-400"></div>
          </div>
        ) : filteredBowlers.length === 0 ? (
          <p className="text-green-200 text-center py-12">No bowlers found</p>
        ) : (
          <div className="space-y-2">
            {filteredBowlers.map((bowler) => (
              <div
                key={bowler.id}
                onClick={() => handleBowlerClick(bowler)}
                className={`w-full text-left p-3 rounded-lg transition-all cursor-pointer ${
                  selectedBowler?.id === bowler.id
                    ? 'bg-green-600/50 border-2 border-green-400'
                    : 'bg-white/5 hover:bg-white/10 border-2 border-transparent'
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-white font-semibold text-sm truncate">
                    {bowler.name} {bowler.is_subscriber ? '👑' : ''}
                  </p>
                </div>
                <p className="text-green-200 text-xs truncate">{bowler.email}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-green-300 text-xs">{bowler.league_count} {t('superadmin.bowlers.leaguesRegistered')}</p>
                  <p className="text-green-400 text-xs">#{bowler.id}</p>
                </div>
                {bowler.is_subscriber && bowler.subscription_expiration ? (
                  <p className="text-yellow-300 text-xs mt-0.5">🔔 {new Date(bowler.subscription_expiration).toLocaleDateString()}</p>
                ) : null}
                <div className="flex gap-1.5 mt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBowler(bowler);
                      setEditForm({
                        name: bowler.name,
                        email: bowler.email,
                        city: bowler.city || '',
                        country: bowler.country || '',
                        gender: bowler.gender || '',
                        dob: bowler.dob ? bowler.dob.split('T')[0] : ''
                      });
                      setEditError(null);
                      setShowEditModal(true);
                    }}
                    className="text-xs px-2 py-0.5 bg-green-500/20 hover:bg-green-500/40 border border-green-400/40 text-green-300 rounded transition"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openSetPwdModal(bowler); }}
                    className="text-xs px-2 py-0.5 bg-yellow-500/20 hover:bg-yellow-500/40 border border-yellow-400/40 text-yellow-300 rounded transition"
                  >
                    🔑 Pwd
                  </button>
                  {bowler.league_count === 0 && (
                    deleteConfirmId === bowler.id ? (
                      <span className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteBowler(bowler); }}
                          disabled={deletingBowlerId === bowler.id}
                          className="text-xs px-2 py-0.5 bg-red-600/80 hover:bg-red-500 border border-red-400/60 text-white rounded transition disabled:opacity-50"
                        >
                          {deletingBowlerId === bowler.id ? '…' : 'Confirm'}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(null); }}
                          className="text-xs px-2 py-0.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white/70 rounded transition"
                        >
                          ✕
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteError(null); setDeleteConfirmId(bowler.id); }}
                        className="text-xs px-2 py-0.5 bg-red-500/20 hover:bg-red-500/40 border border-red-400/40 text-red-300 rounded transition"
                      >
                        🗑️ Delete
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Leagues + Inline Stats */}
      <div className="lg:col-span-2 bg-white/10 backdrop-blur-lg rounded-xl p-4 sm:p-6 border border-white/20 overflow-y-auto">
        {selectedBowler ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl sm:text-2xl font-bold text-white truncate">
                {selectedBowler.name}'s {t('superadmin.bowlers.leagues')}
              </h3>
              <button
                onClick={() => { setSelectedBowler(null); setSelectedLeague(null); setBowlerLeagues([]); setBowlerStats(null); }}
                className="text-white/40 hover:text-white transition shrink-0 ml-2 text-lg leading-none"
                title="Clear selection"
              >
                ✕
              </button>
            </div>

            {loadingLeagues ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-400"></div>
              </div>
            ) : bowlerLeagues.length === 0 ? (
              <p className="text-green-200 text-center py-12">{t('superadmin.bowlers.noLeagues')}</p>
            ) : (
              <div className="space-y-3">
                {bowlerLeagues.map((league) => (
                  <div key={league.league_id}>
                    <button
                      onClick={() => handleLeagueClick(league)}
                      className={`w-full text-left p-4 rounded-lg transition-all ${
                        selectedLeague?.league_id === league.league_id
                          ? 'bg-blue-600/50 border-2 border-blue-400 rounded-b-none'
                          : 'bg-white/5 hover:bg-white/10 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-white font-bold truncate">{league.league_name}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs shrink-0 ml-2 ${getLeagueStatus(league.start_date, league.end_date).bgColor} ${getLeagueStatus(league.start_date, league.end_date).color}`}>
                          {getLeagueStatus(league.start_date, league.end_date).status === 'upcoming' && t('common.upcoming')}
                          {getLeagueStatus(league.start_date, league.end_date).status === 'active' && t('common.active')}
                          {getLeagueStatus(league.start_date, league.end_date).status === 'completed' && t('common.completed')}
                        </span>
                      </div>
                      <p className="text-green-200 text-sm">{league.league_venue} · {league.league_city}, {league.league_country}</p>
                      <p className="text-green-300 text-xs mt-1">👥 {t('superadmin.bowlers.team')}: {league.team_name} · {league.member_role}</p>
                    </button>

                    {/* Stats panel — expands inline below the selected league */}
                    {selectedLeague?.league_id === league.league_id && (
                      <div className="bg-blue-900/20 border-2 border-blue-400 border-t-0 rounded-b-lg p-4">
                        {loadingStats ? (
                          <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-400"></div>
                          </div>
                        ) : bowlerStats ? (
                          <div className="space-y-4">
                            {/* Personal Stats */}
                            <div className="bg-gradient-to-br from-blue-600/30 to-purple-600/30 p-4 rounded-lg border border-blue-400/50">
                              <h4 className="text-white font-bold mb-3">📊 {t('superadmin.bowlers.personalStats')}</h4>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <div className="bg-white/10 p-2 rounded text-center">
                                  <p className="text-blue-200 text-xs">{t('superadmin.bowlers.average')}</p>
                                  <p className="text-white text-xl font-bold">
                                    {bowlerStats.bowlerStats.average_score ? Number(bowlerStats.bowlerStats.average_score).toFixed(1) : 'N/A'}
                                  </p>
                                </div>
                                <div className="bg-white/10 p-2 rounded text-center">
                                  <p className="text-blue-200 text-xs">{t('superadmin.bowlers.handicap')}</p>
                                  <p className="text-white text-xl font-bold">
                                    {bowlerStats.bowlerStats.average_handicap !== null && bowlerStats.bowlerStats.average_handicap !== undefined ? bowlerStats.bowlerStats.average_handicap : 'N/A'}
                                  </p>
                                </div>
                                <div className="bg-white/10 p-2 rounded text-center">
                                  <p className="text-blue-200 text-xs">{t('superadmin.bowlers.totalGames')}</p>
                                  <p className="text-white text-xl font-bold">{bowlerStats.bowlerStats.total_games || 0}</p>
                                </div>
                                <div className="bg-white/10 p-2 rounded text-center">
                                  <p className="text-blue-200 text-xs">{t('superadmin.bowlers.totalPins')}</p>
                                  <p className="text-white text-xl font-bold">{bowlerStats.bowlerStats.total_pins || 0}</p>
                                </div>
                              </div>
                              {bowlerStats.leagueSettings.handicap_enabled && (
                                <div className="mt-3 bg-white/10 p-2 rounded">
                                  <p className="text-blue-200 text-xs mb-1">{t('superadmin.bowlers.handicapSettings')}:</p>
                                  <div className="grid grid-cols-3 gap-2 text-xs">
                                    <p className="text-white">{t('superadmin.bowlers.base')}: {bowlerStats.leagueSettings.handicap_base}</p>
                                    <p className="text-white">{t('superadmin.bowlers.percentage')}: {bowlerStats.leagueSettings.handicap_percentage}%</p>
                                    <p className="text-white">{t('superadmin.bowlers.max')}: {bowlerStats.leagueSettings.handicap_max}</p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Team Members */}
                            <div className="bg-white/5 p-4 rounded-lg">
                              <h4 className="text-white font-bold mb-3">👥 {t('superadmin.bowlers.myTeam')}: {bowlerStats.teamInfo.team_name}</h4>
                              <div className="space-y-2">
                                {bowlerStats.teamMembers.map((member) => (
                                  <div key={member.player_id} className="bg-white/5 p-3 rounded flex justify-between items-center">
                                    <div>
                                      <p className="text-white font-semibold text-sm">
                                        {member.player_name} {member.is_subscriber ? '👑' : ''}
                                      </p>
                                      <p className="text-green-200 text-xs">{member.player_email}</p>
                                      <p className="text-green-300 text-xs">{member.city}, {member.country}</p>
                                      {member.is_subscriber && member.subscription_expiration ? (
                                        <p className="text-yellow-300 text-xs">Expires: {new Date(member.subscription_expiration).toLocaleDateString()}</p>
                                      ) : null}
                                    </div>
                                    <div className="text-right shrink-0 ml-2">
                                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                        member.payment_status === 'completed' ? 'bg-green-500/20 text-green-300' :
                                        member.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                                        'bg-red-500/20 text-red-300'
                                      }`}>
                                        {member.payment_status}
                                      </span>
                                      {member.amount_paid > 0 && (
                                        <p className="text-green-300 text-xs mt-0.5">${member.amount_paid}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Other Participants */}
                            <button
                              onClick={() => fetchOtherParticipants(selectedBowler.id, league.league_id)}
                              className="w-full p-3 bg-purple-600/30 hover:bg-purple-600/50 rounded-lg text-white text-sm font-semibold transition"
                            >
                              🔍 {t('superadmin.bowlers.viewOtherParticipants')}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="text-6xl mb-4">🎳</span>
            <p className="text-green-200 text-lg">{t('superadmin.bowlers.selectBowler')}</p>
            <p className="text-green-300/60 text-sm mt-1">Pick a bowler from the list to see their leagues</p>
          </div>
        )}
      </div>

      {/* Edit Bowler Modal */}
      {showEditModal && selectedBowler && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md w-full mx-4 border border-white/20" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-6">✏️ Edit Bowler</h2>

            {editError && (
              <p className="text-red-300 text-sm mb-4 bg-red-500/20 p-3 rounded-lg">{editError}</p>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-green-200 text-sm font-semibold mb-1">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-400"
                />
              </div>
              <div>
                <label className="block text-green-200 text-sm font-semibold mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-400"
                />
              </div>
              <div>
                <label className="block text-green-200 text-sm font-semibold mb-1">City</label>
                <input
                  type="text"
                  value={editForm.city}
                  onChange={e => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-400"
                />
              </div>
              <div>
                <label className="block text-green-200 text-sm font-semibold mb-1">Country</label>
                <input
                  type="text"
                  value={editForm.country}
                  onChange={e => setEditForm(prev => ({ ...prev, country: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-400"
                />
              </div>
              <div>
                <label className="block text-green-200 text-sm font-semibold mb-1">Gender</label>
                <select
                  value={editForm.gender}
                  onChange={e => setEditForm(prev => ({ ...prev, gender: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-400"
                >
                  <option value="">— Not specified —</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-green-200 text-sm font-semibold mb-1">Date of Birth</label>
                <input
                  type="date"
                  value={editForm.dob}
                  onChange={e => setEditForm(prev => ({ ...prev, dob: e.target.value }))}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-400"
                />
                {editForm.dob && (
                  <p className="text-green-300 text-xs mt-1">
                    Age: {Math.floor((Date.now() - new Date(editForm.dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000))} years old
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold transition"
              >
                {savingEdit ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-bold transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Other Participants Modal */}
      {showOtherParticipantsModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowOtherParticipantsModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-6xl w-full mx-4 border border-white/20 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-3xl font-bold text-white mb-6">🔍 {t('superadmin.bowlers.allOtherParticipants')}</h2>
            
            {otherParticipants.length === 0 ? (
              <p className="text-white text-center py-12">{t('superadmin.bowlers.noOtherParticipants')}</p>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedOtherParticipants).map(([teamId, data]) => (
                  <div key={teamId} className="bg-white/5 p-4 rounded-lg">
                    <h3 className="text-white font-bold text-lg mb-3">{data.team_name}</h3>
                    <div className="space-y-2">
                      {data.members.map((member) => (
                        <div key={member.player_id} className="bg-white/5 p-3 rounded flex justify-between items-center">
                          <div>
                            <p className="text-white font-semibold">
                              {member.player_name} {member.is_subscriber ? '👑' : ''}
                            </p>
                            <p className="text-green-200 text-sm">{member.player_email}</p>
                            <p className="text-green-300 text-xs">{member.city}, {member.country}</p>
                            {member.is_subscriber && member.subscription_expiration ? (
                              <p className="text-yellow-300 text-xs">Expires: {new Date(member.subscription_expiration).toLocaleDateString()}</p>
                            ) : null}
                          </div>
                          <div className="text-right">
                            <p className="text-white text-sm">
                              {t('superadmin.bowlers.avg')}: {member.average_score ? Number(member.average_score).toFixed(1) : 'N/A'}
                            </p>
                            <p className="text-green-300 text-xs">
                              {t('superadmin.bowlers.hcp')}: {member.average_handicap !== null && member.average_handicap !== undefined ? member.average_handicap : 'N/A'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowOtherParticipantsModal(false)}
              className="w-full mt-6 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-bold transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Set Password Modal */}
      {showSetPwdModal && setPwdTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowSetPwdModal(false)}>
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 max-w-md w-full mx-4 border border-white/20" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-2">🔑 Set Password</h2>
            <p className="text-green-200 text-sm mb-6">
              Setting a password for <strong className="text-white">{setPwdTarget.name}</strong>{' '}
              <span className="text-green-300">({setPwdTarget.email})</span>
            </p>

            {setPwdSuccess ? (
              <div className="text-center py-4">
                <p className="text-green-300 text-lg font-semibold mb-2">✅ Password set successfully!</p>
                <p className="text-green-200 text-sm mb-6">An email was sent to {setPwdTarget.email} with their new password.</p>
                <button
                  onClick={() => setShowSetPwdModal(false)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-bold transition"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {setPwdError && (
                  <p className="text-red-300 text-sm mb-4 bg-red-500/20 p-3 rounded-lg">{setPwdError}</p>
                )}
                <div className="mb-6">
                  <label className="block text-green-200 text-sm font-semibold mb-1">New Password</label>
                  <input
                    type="text"
                    value={setPwdValue}
                    onChange={e => setSetPwdValue(e.target.value)}
                    placeholder="Min. 6 characters"
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-400"
                  />
                  <p className="text-green-300 text-xs mt-1">The bowler will be emailed their new password and told to change it.</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSetPassword}
                    disabled={setPwdLoading}
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold transition"
                  >
                    {setPwdLoading ? 'Setting...' : 'Set Password'}
                  </button>
                  <button
                    onClick={() => setShowSetPwdModal(false)}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-bold transition"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterBowlers;
